import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Cybersecurity",
  "Cloud Infrastructure",
  "AI & Autonomous Agents",
  "Not sure yet",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please fill in your name, email, and service of interest.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received. Our team will be in touch shortly.");
      setForm({ name: "", company: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0B0E14] border border-white/10 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00FFA3] focus:border-transparent px-4 py-3 transition-shadow";
  const labelCls =
    "font-mono-tech text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0B0E14]"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#00FFA3]">
            // Book a Consultation
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mt-4 leading-tight">
            Let's secure your mission.
          </h2>
          <p className="text-slate-400 mt-5 text-lg leading-relaxed">
            Tell us about your goals and challenges. We'll respond with a clear,
            no-obligation plan of action — usually within one business day.
          </p>

          <a
            href="mailto:Cybervets@outlook.com"
            data-testid="contact-email-link"
            className="inline-flex items-center gap-3 mt-8 border border-white/10 bg-[#151921] px-5 py-4 hover:border-[#00FFA3]/40 transition-colors group"
          >
            <Mail size={20} className="text-[#00FFA3]" />
            <div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">
                Email Direct
              </div>
              <div className="text-white group-hover:text-[#00FFA3] transition-colors font-medium">
                Cybervets@outlook.com
              </div>
            </div>
          </a>
        </div>

        <div className="bg-[#151921] border border-white/10 p-8 md:p-10">
          {done ? (
            <div
              data-testid="contact-success"
              className="flex flex-col items-center text-center py-16"
            >
              <CheckCircle2 size={56} className="text-[#00FFA3]" />
              <h3 className="font-heading font-bold text-2xl text-white mt-6">
                Request Received
              </h3>
              <p className="text-slate-400 mt-3 max-w-sm">
                Thank you for reaching out. A member of our team will contact you
                shortly at the email you provided.
              </p>
              <button
                data-testid="contact-reset"
                onClick={() => setDone(false)}
                className="mt-8 border border-[#00FFA3] text-[#00FFA3] font-bold px-6 py-3 hover:bg-[#00FFA3]/10 transition-colors"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input
                  data-testid="contact-name"
                  className={inputCls}
                  placeholder="Jane Doe"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>Company</label>
                <input
                  data-testid="contact-company"
                  className={inputCls}
                  placeholder="Acme Corp"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>Work Email *</label>
                <input
                  data-testid="contact-email"
                  type="email"
                  className={inputCls}
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>Service Needed *</label>
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger
                    data-testid="contact-service"
                    className="w-full bg-[#0B0E14] border border-white/10 text-white rounded-none px-4 py-3 h-auto focus:ring-2 focus:ring-[#00FFA3]"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151921] border-white/10 text-white rounded-none">
                    {services.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        data-testid={`service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="focus:bg-[#00FFA3]/10 focus:text-white"
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>How can we help?</label>
                <textarea
                  data-testid="contact-message"
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Briefly describe your goals or challenges..."
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#00FFA3] text-[#0B0E14] font-bold px-8 py-4 hover:bg-[#00E5FF] transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Book My Consultation <Send size={16} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
